import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { useState, useMemo, useCallback, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "./Context";
import type { LoginType } from "./types";
import { decodeUserInfo } from "../../utils/user";
import { applyTheme, themeTemplates } from "../../styles";
import { getTokenExpiryMs, useTokenExpiry } from "./utils";

const ONBOARDING_STORAGE_KEY = "onboarding_state";

interface PersistedOnboarding {
  mustChangePassword: boolean;
  mustAcceptTerms: boolean;
}

function getValidToken(): string | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem("auth_token");
  if (!stored) return null;
  const expMs = getTokenExpiryMs(stored);
  if (expMs && expMs <= Date.now()) {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("theme_template");
    localStorage.removeItem("language");
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    return null;
  }
  return stored;
}

function getPersistedOnboarding(): PersistedOnboarding {
  if (typeof window === "undefined") {
    return { mustChangePassword: false, mustAcceptTerms: false };
  }
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return { mustChangePassword: false, mustAcceptTerms: false };
    const parsed = JSON.parse(raw) as Partial<PersistedOnboarding>;
    return {
      mustChangePassword: Boolean(parsed.mustChangePassword),
      mustAcceptTerms: Boolean(parsed.mustAcceptTerms),
    };
  } catch {
    return { mustChangePassword: false, mustAcceptTerms: false };
  }
}

function persistOnboarding(state: PersistedOnboarding) {
  if (typeof window === "undefined") return;
  localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(getValidToken);
  const [onboarding, setOnboarding] =
    useState<PersistedOnboarding>(getPersistedOnboarding);
  const queryClient = useQueryClient();

  const authenticated = Boolean(token);

  const login = useCallback(
    (data: LoginType) => {
      queryClient.clear();
      setToken(data.token);
      localStorage.setItem("auth_token", data.token);

      const nextOnboarding: PersistedOnboarding = {
        mustChangePassword: Boolean(data.mustChangePassword),
        mustAcceptTerms: Boolean(data.mustAcceptTerms),
      };
      setOnboarding(nextOnboarding);
      persistOnboarding(nextOnboarding);

      try {
        const userInfo = decodeUserInfo(data.token);
        if (
          userInfo.theme &&
          themeTemplates[userInfo.theme as keyof typeof themeTemplates]
        ) {
          const theme = userInfo.theme as keyof typeof themeTemplates;
          localStorage.setItem("theme_template", theme);
          applyTheme(themeTemplates[theme].colors);
        }
        if (userInfo.language) {
          localStorage.setItem("language", userInfo.language.toLowerCase());
        }
        Sentry.setUser({
          id: userInfo.publicId ?? undefined,
          email: userInfo.email ?? undefined,
          gymId: userInfo.gymPublicId ?? undefined,
          role: userInfo.role ?? undefined,
        });
        if (userInfo.publicId) {
          posthog.identify(userInfo.publicId, {
            email: userInfo.email,
            role: userInfo.role,
            gymPublicId: userInfo.gymPublicId,
            gymName: userInfo.gymName,
          });
        }
      } catch (err) {
        console.error("Error applying config from token:", err);
      }
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    setToken(null);
    setOnboarding({ mustChangePassword: false, mustAcceptTerms: false });
    localStorage.removeItem("auth_token");
    localStorage.removeItem("theme_template");
    localStorage.removeItem("language");
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    applyTheme(themeTemplates.dark_yellow.colors);
    Sentry.setUser(null);
    posthog.reset();
    queryClient.clear();
  }, [queryClient]);

  const markPasswordChanged = useCallback((mustAcceptTerms: boolean) => {
    const next: PersistedOnboarding = {
      mustChangePassword: false,
      mustAcceptTerms: Boolean(mustAcceptTerms),
    };
    setOnboarding(next);
    persistOnboarding(next);
  }, []);

  const markLegalAccepted = useCallback(() => {
    const next: PersistedOnboarding = {
      mustChangePassword: false,
      mustAcceptTerms: false,
    };
    setOnboarding(next);
    persistOnboarding(next);
  }, []);

  const syncOnboardingFromGate = useCallback((state: PersistedOnboarding) => {
    const next: PersistedOnboarding = {
      mustChangePassword: Boolean(state.mustChangePassword),
      mustAcceptTerms: Boolean(state.mustAcceptTerms),
    };
    setOnboarding(next);
    persistOnboarding(next);
  }, []);

  useTokenExpiry(token, logout);

  const value = useMemo(
    () => ({
      token,
      authenticated,
      mustChangePassword: onboarding.mustChangePassword,
      mustAcceptTerms: onboarding.mustAcceptTerms,
      login,
      logout,
      markPasswordChanged,
      markLegalAccepted,
      syncOnboardingFromGate,
    }),
    [
      token,
      authenticated,
      onboarding,
      login,
      logout,
      markPasswordChanged,
      markLegalAccepted,
      syncOnboardingFromGate,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
